import React from 'react';
import { Dropdown } from 'antd';
import {
  HomeOutlined,
  HomeFilled,
  RadarChartOutlined,
  FormOutlined,
  AppstoreOutlined,
  AppstoreFilled,
  LogoutOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { User } from '../types';
import { brand, neutral } from '../theme/tokens';
import { FEATURE_FLAGS } from '../featureFlags';
// ⚠️这个 import 必须留着：ParentDashboard.css 里的 .parent-shell>header / >nav
//   两组 !important 规则改的是**本文件**渲染的 header 和 nav（六宫格首页要窄壳+浅底），
//   写在 ParentDashboard.tsx 里管不到父级元素。
//   作用域靠下面那个只在家长身上加的 .parent-shell class 兜住 ——
//   规则全部带 .parent-shell 前缀，学生/老师不加这个 class 就完全不受影响。
import '../pages/ParentDashboard.css';

const tabs = [
  { key: '/home', icon: <HomeOutlined />, activeIcon: <HomeFilled />, label: '首页' },
  // 成长画像受 ENABLE_GROWTH_PROFILE 控制。关闭的理由不是"功能没做完"，
  // 而是数据源没了：61/62 个家庭点进去是 Empty（/adaptive/latest 404）。
  // 详见 featureFlags.ts 里的实测数字。
  ...(FEATURE_FLAGS.ENABLE_GROWTH_PROFILE
    ? [{ key: '/home/profile', icon: <RadarChartOutlined />, activeIcon: <RadarChartOutlined />, label: '成长画像' }]
    : []),
  { key: '/home/services', icon: <AppstoreOutlined />, activeIcon: <AppstoreFilled />, label: '家校服务' },
  // 测评入口受 FEATURE_FLAGS.ENABLE_ASSESSMENT 控制，关闭时整个 tab 不渲染
  ...(FEATURE_FLAGS.ENABLE_ASSESSMENT
    ? [{ key: '/home/assessment', icon: <FormOutlined />, activeIcon: <FormOutlined />, label: '测评' }]
    : []),
];

// 家校服务的子路由，激活时高亮“家校服务”tab
// /home/brain 保留在列表里：开关打开时它仍然是家校服务的子页面，
// 关闭时路由不存在、走不到这儿，留着不影响，省得将来开回来又忘了加
const SERVICE_PATHS = ['/home/services', '/home/courses', '/home/leave', '/home/chain', '/home/album', '/home/mailbox', '/home/family', '/home/brain', '/home/schedule'];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user: User = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const activeKey = (() => {
    const p = location.pathname;
    if (SERVICE_PATHS.some((sp) => p === sp || p.startsWith(sp + '/'))) return '/home/services';
    if (p === '/home') return '/home';
    if (p.startsWith('/home/profile')) return '/home/profile';
    if (p.startsWith('/home/assessment')) return '/home/assessment';
    return '/home';
  })();

  // 六宫格首页的窄壳样式只给家长。学生账号虽然也能走 /home
  // （PrivateRoute roles=['student','parent']），但线上 62 个 student 行的 username
  // 都是 `_stu_` 前缀、password 从不对外发（studentAdmin.js:239 原话
  // 「username 不对外发」），实际登录的只有 62 个家长账号。
  // 即便如此也按角色判而不是无条件加 —— 将来真发学生号时不会连带套上家长文案的壳。
  const isParentShell = user.role === 'parent' && !FEATURE_FLAGS.ENABLE_GROWTH_PROFILE;

  const userMenu = {
    items: [
      {
        key: 'password',
        icon: <LockOutlined />,
        label: '修改密码',
        onClick: () => navigate('/change-password'),
      },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
    ],
  };

  // 2026-09-28: 这里**没有**「家长访问 /home/profile 就重定向回首页」那段。
  // 改版初版有，摘掉了，原因是那是错的层级：
  //   - /home/profile 关不关由 ENABLE_GROWTH_PROFILE 在 App.tsx 决定要不要注册路由，
  //     不注册时 `path="*"` 兜底自然会回首页，不需要在布局里再拦一次；
  //   - 写在这里反而会连 /home/family（填问卷入口，是成长大脑唯一数据来源，
  //     刻意不能关）一起弹掉。
  // 一句话：路由的事在路由层解决，布局层只管壳。

  return (
    <div
      className={isParentShell ? 'parent-shell' : undefined}
      style={{ minHeight: '100vh', background: neutral[50], paddingBottom: 72 }}
    >
      {/* 顶部极简 header：校徽 + 名字 + 退出 */}
      <header
        style={{
          height: 52, background: neutral[0], borderBottom: `1px solid ${neutral[100]}`,
          display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10,
          position: 'sticky', top: 0, zIndex: 50,
        }}
      >
        <img src="/school-badge.gif" alt="校徽" style={{ width: 30, height: 30, objectFit: 'contain' }} />
        <span style={{ fontSize: 15, fontWeight: 600, color: neutral[800], flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          成长指导平台
        </span>
        <Dropdown menu={userMenu} trigger={['click']}>
          <span style={{ fontSize: 13, color: neutral[600], cursor: 'pointer' }}>{user.name} ▾</span>
        </Dropdown>
      </header>

      <Outlet />

      {/* 底部导航 */}
      <nav
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          borderTop: `1px solid ${neutral[100]}`,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          padding: '6px 0 env(safe-area-inset-bottom, 6px)', zIndex: 100,
          boxShadow: '0 -2px 12px rgba(15,23,42,0.04)',
        }}
      >
        {/* tab 的增减不在这里 .filter()，而是在上面 tabs 数组里按开关拼 ——
            两处都能滤会导致"开关打开了但 tab 还是不出现"这种对不上的状态。 */}
        {tabs.map((tab) => {
          const active = activeKey === tab.key;
          return (
            <div
              key={tab.key}
              onClick={() => navigate(tab.key)}
              // data-active 是给 ParentDashboard.css 的
              // `.parent-shell>nav [data-active="true"]` 用的（六宫格壳下选中态换成金色）。
              // ⚠️那条 CSS 规则以前一直是死的 —— 这里从来没输出过 data-active 属性，
              //   只有内联 style 的 color。加上它规则才真正生效。
              data-active={active ? 'true' : 'false'}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                cursor: 'pointer', padding: '4px 12px', minWidth: 56,
                color: active ? brand[600] : neutral[400], transition: 'color 0.2s',
              }}
            >
              <span style={{ fontSize: 21, lineHeight: 1 }}>{active ? tab.activeIcon : tab.icon}</span>
              <span style={{ fontSize: 11, marginTop: 3, fontWeight: active ? 600 : 400 }}>{tab.label}</span>
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default AppLayout;
